import { PlayArrow } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Box, Button, Drawer, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { WindowDrawerProps } from '../../../@types/ContextProviderRelated';
import { EUnitType } from '../../../@types/Enums';
import { useAttributeActions } from '../../../hooks/useAttributeActions';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { getDefaultObjectId, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { deleteButton, immersightBlue } from '../../../utils/colors';
import { MAX_WINDOW_LENGTH, MAX_WINDOW_LENGTH_IMPERIAL, MIN_WINDOW_LENGTH, MIN_WINDOW_LENGTH_IMPERIAL } from '../../../utils/constants';
import { cmToInternalRounded, getLocalizedUnitLabel, inchToInternalRounded, internalToCentimetersRounded, internalToInchesRounded } from '../../../utils/conversionUtils';
import filesApiService from '../../../utils/filesApiService';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';

const WindowDrawer = ({ open, onClose, selectedWindow, resetSelection, id }: WindowDrawerProps): JSX.Element => {
  const { building, updateWindowWidth, deleteWindow, updateWindowAttributes, defaultObjects } = useBuilding();
  const wallIndex = selectedWindow.wallIndex;
  const windowIndex = selectedWindow.windowIndex;
  const wall = building.walls[wallIndex] as IWall;
  const window = wall?.windows?.[windowIndex];
  const { unitType } = useUnitContext();
  const initialWindowWidth = window?.length ? internalToCentimetersRounded(window.length) : MIN_WINDOW_LENGTH;
  const initialWindowWidthImperial = window?.length ? internalToInchesRounded(window.length) : MIN_WINDOW_LENGTH_IMPERIAL;
  const [windowWidth, setWindowWidth] = useState<number>(unitType === EUnitType.METRIC ? initialWindowWidth : initialWindowWidthImperial);
  const intl = useIntl();
  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'centimeter'), [intl]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'inch'), [intl]);

  // Attribute state
  const [attributes, setAttributes] = useState<AttributeModel[]>(window?.attributes ?? []);
  const [attributeValues, setAttributeValues] = useState<Record<number, string>>(
    (window?.attributes ?? []).reduce(
      (acc, a) => {
        if (a.value) acc[a.id] = `${a.value}`;
        return acc;
      },
      {} as Record<number, string>,
    ),
  );
  const [showInputFields, setShowInputFields] = useState<Record<number, boolean>>(
    (window?.attributes ?? []).reduce(
      (acc, a) => {
        acc[a.id] = !!a.value;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );
  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [attrSelected, setAttrSelected] = useState<IAttribute | null>(null);
  const [attrInputValue, setAttrInputValue] = useState('');
  const [attrError, setAttrError] = useState<string | null>(null);
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  // Use attribute actions hook
  const { handleAttributeValueChange, handleDeleteAttribute, toggleInputField, handleAttributeSelection, getSortedAttributes } = useAttributeActions(
    attributes,
    setAttributes,
    setAttributeValues,
    setShowInputFields,
    setAttrSelected,
    setAttrInputValue,
    (attrs) => updateWindowAttributes(wallIndex, windowIndex, attrs),
    allAttributes,
    setAllAttributes,
    setAttrError,
  );
  const localizedWindowName = useMemo(() => {
    const objId = getDefaultObjectId(ObjectType.Window);
    const apiObj = defaultObjects?.find((o: any) => o.id === objId);
    const { locale } = intl;

    if (apiObj && apiObj.localization) {
      const currentLocale = locale.split('-')[0];
      return apiObj.localization[currentLocale] || apiObj.localization.en || apiObj.name;
    }

    return intl.formatMessage({ id: 'WindowDrawer.window', defaultMessage: 'Fenster' });
  }, [defaultObjects, intl]);
  // --- NEW: Handle Attribute Unit Change ---
  const handleAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      const updated = attributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit, // Update Unit
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );
      setAttributes(updated);
      updateWindowAttributes(wallIndex, windowIndex, updated);
    },
    [attributes, updateWindowAttributes, wallIndex, windowIndex],
  );

  useEffect(() => {
    if (wall && window) {
      if (unitType === EUnitType.METRIC) {
        const newWidth = window.length ? internalToCentimetersRounded(window.length) : MIN_WINDOW_LENGTH;
        setWindowWidth(newWidth);
      } else {
        const newWidth = window.length ? internalToInchesRounded(window.length) : MIN_WINDOW_LENGTH_IMPERIAL;
        setWindowWidth(newWidth);
      }
      setAttributes(window.attributes ?? []);
      setAttributeValues(
        (window.attributes ?? []).reduce(
          (acc, a) => {
            if (a.value) acc[a.id] = `${a.value}`;
            return acc;
          },
          {} as Record<number, string>,
        ),
      );
      setShowInputFields(
        (window.attributes ?? []).reduce(
          (acc, a) => {
            acc[a.id] = !!a.value;
            return acc;
          },
          {} as Record<number, boolean>,
        ),
      );
    } else {
      onClose();
    }
  }, [windowIndex, wallIndex, unitType, window, wall, onClose]);

  // Fetch full attribute catalog when drawer opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const catalog = await filesApiService.getAttributes(regionCode);
        setAllAttributes(catalog);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error fetching attributes: ${(e as Error).message}`);
      }
    })();
  }, [open, regionCode]);

  const validateAndSet = useCallback(
    (value: number): void => {
      const minValue = unitType === EUnitType.METRIC ? MIN_WINDOW_LENGTH : MIN_WINDOW_LENGTH_IMPERIAL;
      const maxValue = unitType === EUnitType.METRIC ? MAX_WINDOW_LENGTH : MAX_WINDOW_LENGTH_IMPERIAL;

      if (!isNaN(value)) {
        if (value < minValue) {
          value = minValue;
        } else if (value > maxValue) {
          value = maxValue;
        }
        const roundedInternalValue = unitType === EUnitType.METRIC ? cmToInternalRounded(value) : inchToInternalRounded(value);
        setWindowWidth(value);
        updateWindowWidth(wallIndex, windowIndex, roundedInternalValue);
      } else {
        setWindowWidth(unitType === EUnitType.METRIC ? initialWindowWidth : initialWindowWidthImperial);
      }
    },
    [unitType, initialWindowWidth, initialWindowWidthImperial, updateWindowWidth, wallIndex, windowIndex],
  );

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value, 10);
    if (!isNaN(newValue)) {
      setWindowWidth(newValue);
    }
  }, []);

  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const value = parseInt(event.target.value, 10);
      validateAndSet(value);
    },
    [validateAndSet],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      const value = parseInt((event.target as HTMLInputElement).value, 10);
      if (event.key === 'Enter') {
        validateAndSet(value);
      } else if (!isNaN(value)) {
        setWindowWidth(value);
      }
    },
    [validateAndSet],
  );

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [onClose, resetSelection]);

  const handleOnDelete = useCallback(() => {
    deleteWindow(wallIndex, windowIndex);
    resetSelection();
    onClose();
  }, [wallIndex, windowIndex, deleteWindow, resetSelection, onClose]);

  const wheelConfig: WheelInputConfig = {
    unitType,
    minMetric: MIN_WINDOW_LENGTH,
    maxMetric: MAX_WINDOW_LENGTH,
    minInches: MIN_WINDOW_LENGTH_IMPERIAL,
    maxInches: MAX_WINDOW_LENGTH_IMPERIAL,
  };

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: '64px',
          zIndex: 1000,
          width: '420px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              {localizedWindowName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container item direction="column" spacing={3} p={3}>
        <Grid item>
          <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
            <FormattedMessage id="WindowDrawer.windowWidth" values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }} />
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={windowWidth}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyUp={handleKeyPress}
            onWheel={(e) =>
              handleWheelInput(
                e,
                windowWidth.toString(),
                (v: string) => {
                  const newVal = parseInt(v, 10);
                  if (!isNaN(newVal)) {
                    setWindowWidth(newVal);
                  }
                },
                wheelConfig,
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SyncAltIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: unitType === EUnitType.METRIC ? MIN_WINDOW_LENGTH : MIN_WINDOW_LENGTH_IMPERIAL,
                max: unitType === EUnitType.METRIC ? MAX_WINDOW_LENGTH : MAX_WINDOW_LENGTH_IMPERIAL,
              },
            }}
          />
        </Grid>

        <Grid item>
          <AttributeList
            attributes={getSortedAttributes(attributes)}
            attributeValues={attributeValues}
            showInputFields={showInputFields}
            onAttributeValueChange={handleAttributeValueChange}
            onAttributeUnitChange={handleAttributeUnitChange} // Wired Up!
            onDeleteAttribute={handleDeleteAttribute}
            onToggleInputField={toggleInputField}
            onReorderAttributes={(newAttrs) => {
              setAttributes(newAttrs);
              updateWindowAttributes(wallIndex, windowIndex, newAttrs);
            }}
          />
        </Grid>
        <Grid item>
          <Box
            sx={{
              mt: -5,
              mb: 1,
              width: '99%',
              mx: 'auto',
            }}
          >
            <AttributeAutocomplete
              attributes={allAttributes}
              disabledAttributeIds={attributes.map((attribute) => attribute.id)}
              selected={attrSelected}
              inputValue={attrInputValue}
              error={attrError}
              onChange={handleAttributeSelection}
              onInputChange={(val) => {
                setAttrInputValue(val);
                if (val !== attrSelected?.key) setAttrSelected(null);
              }}
              placeholder={intl.formatMessage({ id: 'WindowDrawer.addAttribute', defaultMessage: 'Add attribute' })}
            />
          </Box>
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleOnDelete}
            sx={{
              backgroundColor: deleteButton,
              marginTop: '20px',
            }}
          >
            <FormattedMessage id="WindowDrawer.deleteWindow" />
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default WindowDrawer;
